import React from 'react';

import { CustomerEmailField } from './customer_email_field';
import { CustomerNameField } from './customer_name_field';
import { PhoneRow } from './phone_row';
import { ConsentField, Customer, Phone, PhoneType } from './types';
import { getNonDeletedPhones, isEmptyCustomer } from './utils';

interface CustomerRowProps {
  customer: Customer;
  onUpdateCustomerField: (customerTempId: string, field: keyof Customer, value: string) => void;
  onUpdatePhoneField: (
    customerTempId: string,
    phoneTempId: string,
    field: keyof Phone,
    value: string | PhoneType,
  ) => void;
  onTogglePrimaryPhone: (phoneTempId: string) => void;
  onTogglePrimaryEmail: (customerTempId: string) => void;
  onDeletePhone: (customerTempId: string, phone: Phone) => void;
  onAddPhone: (customerTempId: string) => void;
  onConsentFieldChange: (
    customerTempId: string,
    phoneTempId: string,
    consentField: ConsentField,
    checked: boolean,
  ) => void;
  onConsentInfoClick: (field: ConsentField) => void;
}

/**
 * Determines if the add phone button should be shown for a phone.
 *
 * @param customer - The customer containing the phone.
 * @param phone - The phone to check.
 * @returns True if the add button should be shown.
 */
const shouldShowAddPhone = (customer: Customer, phone: Phone): boolean => {
  const visiblePhones = getNonDeletedPhones(customer.phones);
  return phone === visiblePhones[visiblePhones.length - 1];
};

/**
 * Determines if the delete phone button should be shown for a phone.
 *
 * @param customer - The customer containing the phone.
 * @param phone - The phone to check.
 * @returns True if the delete button should be shown.
 */
const shouldShowDeletePhone = (customer: Customer, phone: Phone): boolean => {
  return !!(phone.number || phone.id || getNonDeletedPhones(customer.phones).length > 1);
};

/**
 * Component for rendering a single customer row with all its fields.
 *
 * @param props - The component props.
 * @returns The CustomerRow component.
 */
export const CustomerRow: React.FC<CustomerRowProps> = ({
  customer,
  onUpdateCustomerField,
  onUpdatePhoneField,
  onTogglePrimaryPhone,
  onTogglePrimaryEmail,
  onDeletePhone,
  onAddPhone,
  onConsentFieldChange,
  onConsentInfoClick,
}) => {
  const isRequired = !isEmptyCustomer(customer);
  const visiblePhones = getNonDeletedPhones(customer.phones);

  return (
    <tr className="customer-row">
      {/* Name */}
      <td className="end-of-section">
        <CustomerNameField
          name={customer.name}
          required={isRequired}
          onChange={(value) => onUpdateCustomerField(customer.tempId, 'name', value)}
        />
      </td>

      {/* Phones */}
      <td className="end-of-section">
        <table className="table">
          <tbody>
            {visiblePhones.map((phone) => (
              <PhoneRow
                key={phone.tempId}
                phone={phone}
                customerTempId={customer.tempId}
                showAddButton={shouldShowAddPhone(customer, phone)}
                showDeleteButton={shouldShowDeletePhone(customer, phone)}
                onUpdateField={(phoneTempId, field, value) =>
                  onUpdatePhoneField(customer.tempId, phoneTempId, field, value)
                }
                onTogglePrimary={onTogglePrimaryPhone}
                onDelete={(p) => onDeletePhone(customer.tempId, p)}
                onAdd={() => onAddPhone(customer.tempId)}
                onConsentChange={(phoneTempId, consentField, checked) =>
                  onConsentFieldChange(customer.tempId, phoneTempId, consentField, checked)
                }
                onConsentInfoClick={onConsentInfoClick}
              />
            ))}
          </tbody>
        </table>
      </td>

      {/* Email */}
      <td>
        <CustomerEmailField
          customerId={customer.id}
          email={customer.email}
          isPrimaryEmail={customer.primary_email}
          required={isRequired}
          onChange={(value) => onUpdateCustomerField(customer.tempId, 'email', value)}
          onTogglePrimary={() => onTogglePrimaryEmail(customer.tempId)}
        />
      </td>
    </tr>
  );
};
